<route>
  {
    "meta": {
      "permission": [
        "equipements.add_maintenance",
        "equipements.change_maintenance"
      ]
    }
  }
</route>

<template>
  <v-container fluid>
    <v-card>
      <ValidationObserver ref="formObserver" v-slot="{ handleSubmit }">
        <form @submit.prevent="handleSubmit(submit)">
          <i-toolbar :title="toolbarTitle" :loading="load"></i-toolbar>
          <v-card-text>
            <v-row class="py-5">
              <!-- Fecha de inicio -->
              <v-col cols="12" sm="3">
                <i-date-picker
                  v-model="maintenance.start_date"
                  :load="load"
                  :label="
                    maintenance.recurrent ? $tc('start_date', 2) : $t('date')
                  "
                  color="primary"
                />
              </v-col>

              <!-- Modo: Preventivo / Correctivo -->
              <v-col cols="12" sm="3">
                <ValidationProvider
                  vid="mode"
                  :name="$t('mode')"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <v-select
                    outlined
                    :error-messages="errors[0]"
                    v-model="maintenance.mode"
                    item-text="state"
                    item-value="abbr"
                    :items="maintenanceModes"
                    :label="$t('mode')"
                  />
                </ValidationProvider>
              </v-col>

              <!-- Switch: Regular vs Recurrente -->
              <v-col cols="12" sm="3">
                <h6>{{ $tc('equipment_maintenance', 1) }}</h6>
                <div class="container-v-switch pt-2">
                  <label
                    :class="maintenance.recurrent ? '' : 'active'"
                    for="maintenance_recurrent"
                  >
                    {{ $t('regular') }}
                  </label>
                  <v-switch
                    id="maintenance_recurrent"
                    class="mt-0 pt-0"
                    color="primary"
                    v-model="maintenance.recurrent"
                  ></v-switch>
                  <label
                    :class="maintenance.recurrent ? 'active' : ''"
                    for="maintenance_recurrent"
                  >
                    {{ $tc('recurrent', 2) }}
                  </label>
                </div>
              </v-col>

              <!-- Frecuencia de recurrencia -->
              <v-col cols="12" sm="3" v-if="maintenance.recurrent">
                <ValidationProvider
                  vid="type_recurrent"
                  :name="$t('type_recurrent')"
                  :rules="{ required: maintenance.recurrent }"
                  v-slot="{ errors }"
                >
                  <v-select
                    autocomplete="off"
                    outlined
                    :error-messages="errors[0]"
                    v-model="maintenance.type_recurrent"
                    item-text="state"
                    item-value="abbr"
                    :items="typeRecurrentOptions"
                    :label="$t('type_recurrent')"
                  ></v-select>
                </ValidationProvider>
              </v-col>

              <!-- Descripción -->
              <v-col cols="12">
                <ValidationProvider
                  vid="description"
                  :name="$tc('description', 1)"
                  rules=""
                  v-slot="{ errors }"
                >
                  <label for="description">{{ $t('description') }}</label>
                  <vue-editor
                    id="description"
                    v-model="maintenance.description"
                    :error-messages="errors[0]"
                    :editorToolbar="customToolbar"
                  />
                  <span class="areaErrors" v-if="errors[0]">{{
                    errors[0]
                  }}</span>
                </ValidationProvider>
              </v-col>
            </v-row>
          </v-card-text>
        </form>
      </ValidationObserver>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import { VueEditor } from 'vue2-editor'

/**
 * Vista de formulario para creación y edición de mantenimientos de equipo.
 * Soporta configuración independiente de recurrencia (regular vs recurrente),
 * periodicidad (semanal, mensual, anual) y tipo (preventivo / correctivo).
 *
 * @author Pedro Contreras <pdrocont@gmail.com>
 * @date 2026-09-29
 */
export default {
  name: 'maintenance-pk',
  components: {
    VueEditor
  },
  data() {
    return {
      load: false,
      loading: false,
      maintenance: {
        start_date: '',
        recurrent_start_date: '',
        operator: '',
        description: '',
        equipement: null,
        equipement_id: 0,
        expense: 0.0,
        payment: 0.0,
        mode: 'preventive',
        recurrent: false,
        type_recurrent: '',
        pay: '',
        push: true
      },
      maintenanceModes: [
        { state: this.$t('preventive'), abbr: 'preventive' },
        { state: this.$t('corrective'), abbr: 'corrective' }
      ],
      typeRecurrentOptions: [
        { state: this.$t('weekly'), abbr: 'week' },
        { state: this.$t('monthly'), abbr: 'month' },
        { state: this.$t('yearly'), abbr: 'year' }
      ],
      customToolbar: [
        [{ header: [] }],
        [
          'bold',
          'italic',
          'underline',
          'strike',
          'blockquote',
          'code',
          'code-block'
        ],
        [
          { align: '' },
          { align: 'center' },
          { align: 'right' },
          { align: 'justify' }
        ],
        [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
        [{ script: 'sub' }, { script: 'super' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ color: [] }, { background: [] }]
      ]
    }
  },
  computed: {
    ...mapGetters({
      getPermissions: 'session/getPermissions',
      isAdmin: 'session/isAdmin',
      me: 'session/me'
    }),

    /**
     * Título reactivo de la barra superior
     *
     * @returns {String} Título con modo y nombre del equipo
     * @author Pedro Contreras <pdrocont@gmail.com>
     * @date 2026-09-10
     */
    toolbarTitle() {
      const equipName = this.maintenance.equipement?.name
        ? ` (${this.$t(this.maintenance.equipement.name)})`
        : ''
      return `${
        this.maintenance.pk
          ? `${this.$t('edit')} ${this.$t('equipment_maintenance')}`
          : this.$t('add', { value: this.$t('equipment_maintenance') })
      }${equipName}`
    }
  },
  watch: {
    /**
     * Limpia la periodicidad de recurrencia si el usuario desactiva el switch
     *
     * @param {Boolean} isRecurrent Nuevo estado del switch
     * @author Pedro Contreras <pdrocont@gmail.com>
     * @date 2026-09-11
     */
    'maintenance.recurrent'(isRecurrent) {
      if (!isRecurrent) {
        this.maintenance.type_recurrent = ''
      }
    }
  },
  methods: {
    /**
     * Envía los datos del formulario de mantenimiento a la API.
     * Gestiona la creación de nuevos mantenimientos o actualización de existentes,
     * transformando el payload y procesando errores con Vee-Validate.
     *
     * @returns {Promise<Void>}
     * @author Pedro Contreras <pdrocont@gmail.com>
     * @date 2026-09-29
     */
    async submit() {
      this.load = true
      try {
        const payload = {
          start_date: this.maintenance.start_date,
          recurrent_start_date:
            this.maintenance.recurrent_start_date ||
            this.maintenance.start_date,
          operator: this.maintenance.operator,
          description: this.maintenance.description || '',
          mode: this.maintenance.mode,
          pay: this.maintenance.pay,
          payment: Number(this.maintenance.payment) || 0,
          expense: Number(this.maintenance.payment) || 0,
          recurrent: Boolean(this.maintenance.recurrent),
          type_recurrent: this.maintenance.recurrent
            ? this.maintenance.type_recurrent
            : '',
          equipement_id:
            this.maintenance.equipement_id ||
            this.maintenance.equipement?.pk ||
            0
        }

        if (this.maintenance.pk) {
          await this.$api.maintenance.edit({
            pk: this.maintenance.pk,
            form: payload
          })
        } else {
          await this.$api.maintenance.create({ form: payload })
        }

        this.$toast.success(
          `${this.$tc('equipment_maintenance', 1)} ${this.$tc(
            this.maintenance.pk ? 'edited' : 'created',
            2
          )}`
        )

        if (!this.maintenance.pk) {
          try {
            new Audio(require('@/../public/sounds/sound.mp3')).play()
          } catch (e) {
            // Audio no crítico
          }
        }

        if (this.$route.query.event_id) {
          try {
            await this.$api.notifications.remove({
              pk: this.$route.query.event_id
            })
          } catch (e) {
            console.error('Failed to remove notification:', e)
          }
        }

        this.$root.$emit('payment-processed')

        this.changeRoute(this.maintenance)
      } catch (error) {
        console.error('Error submitting maintenance:', error)
        if (
          error.response &&
          error.response.status === 400 &&
          this.$refs.formObserver
        ) {
          this.$refs.formObserver.setErrors(error.response.data)
        } else {
          this.$toast.error(this.$t('error_try_again'))
        }
      } finally {
        this.load = false
      }
    },

    /**
     * Inicializa o consulta el registro de mantenimiento según parámetros de ruta
     *
     * @returns {Promise<Void>}
     * @author Pedro Contreras <pdrocont@gmail.com>
     * @date 2026-09-10
     */
    async getMaintenance() {
      this.loading = true
      const validation = this.codification({
        mode: 'decode',
        code: this.$route.params.pk
      })
      const equipmentParam = this.$route.params.equipment
      const parsedEquipment = equipmentParam ? JSON.parse(equipmentParam) : null
      this.maintenance.equipement = parsedEquipment
      if (parsedEquipment) {
        this.maintenance.equipement_id = parsedEquipment.pk
      }

      if (validation === 'create') {
        this.initializeMaintenance(parsedEquipment)
      } else {
        await this.loadMaintenance(validation)
      }
      this.loading = false
    },

    /**
     * Inicializa el estado para un nuevo mantenimiento
     *
     * @param {Object} equipment Equipo asociado
     * @returns {Void}
     * @author Pedro Contreras <pdrocont@gmail.com>
     * @date 2026-09-10
     */
    initializeMaintenance(equipment) {
      if (this.$store.getters['utils/getIsloading']) {
        this.$store.dispatch('utils/actionLoading')
      }
      this.$route.meta.title = this.$t('create', { model: '' })
      this.maintenance = {
        start_date: this.$route.query.date || '',
        recurrent_start_date: this.$route.query.date || '',
        operator: '',
        description: '',
        equipement: equipment,
        equipement_id: equipment ? equipment.pk : 0,
        expense: 0.0,
        payment: 0.0,
        mode: 'preventive',
        recurrent: false,
        type_recurrent: '',
        pay: '',
        push: true
      }
    },

    /**
     * Carga los datos de un mantenimiento existente desde la API
     *
     * @param {String|Number} pk Identificador del mantenimiento
     * @returns {Promise<Void>}
     * @author Pedro Contreras <pdrocont@gmail.com>
     * @date 2026-09-10
     */
    async loadMaintenance(pk) {
      this.$route.meta.title = this.$t('edit', { model: '' })
      try {
        const res = await this.$api.maintenance.show({ pk })
        this.maintenance = {
          ...res.data,
          equipement_id: res.data.equipement_id || res.data.equipement?.pk
        }

        if (this.$route.query.date) {
          this.maintenance.start_date = this.$route.query.date
        }
      } catch (e) {
        this.changeRoute(this.maintenance)
      }
    },

    /**
     * Redirige hacia la lista de mantenimientos del equipo correspondiente
     *
     * @param {Object} item Objeto mantenimiento con información del equipo
     * @returns {Void}
     * @author Pedro Contreras <pdrocont@gmail.com>
     * @date 2026-09-10
     */
    changeRoute(item) {
      const equipmentTarget =
        (item && item.equipement) ||
        this.maintenance.equipement ||
        (this.$route.params.equipment
          ? JSON.parse(this.$route.params.equipment)
          : null)

      if (equipmentTarget && equipmentTarget.pk) {
        this.$router.push({
          name: 'maintenance',
          params: { equipment: JSON.stringify(equipmentTarget) }
        })
      } else {
        this.$router.push({ name: 'equipment' })
      }
    }
  },
  mounted() {
    this.getMaintenance()
  }
}
</script>
